import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';

const Home = () => {
  const { t } = useLanguage();
  const [currentMockup, setCurrentMockup] = useState(0);

  const defaultMockups = [
    'https://raw.githubusercontent.com/delfieuxalexi-afk/alexi-delfieux-portfolio/main/Mockups/Tableau1.jpg',
    'https://raw.githubusercontent.com/delfieuxalexi-afk/alexi-delfieux-portfolio/main/Mockups/Tableau2.jpg',
    'https://raw.githubusercontent.com/delfieuxalexi-afk/alexi-delfieux-portfolio/main/Mockups/Tableau3.jpg',
    'https://raw.githubusercontent.com/delfieuxalexi-afk/alexi-delfieux-portfolio/main/Mockups/Tableau4.jpg',
    'https://raw.githubusercontent.com/delfieuxalexi-afk/alexi-delfieux-portfolio/main/Mockups/Tableau5.jpg',
    'https://raw.githubusercontent.com/delfieuxalexi-afk/alexi-delfieux-portfolio/main/Mockups/Tableau6.jpg'
  ];

  // Load carousel images from localStorage or use defaults
  const savedCarousel = localStorage.getItem('carouselImages');
  const mockups = savedCarousel ? JSON.parse(savedCarousel) : defaultMockups;

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMockup((prev) => (prev + 1) % mockups.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [mockups.length]);

  const nextMockup = () => {
    setCurrentMockup((prev) => (prev + 1) % mockups.length);
  };

  const prevMockup = () => {
    setCurrentMockup((prev) => (prev - 1 + mockups.length) % mockups.length);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section with Mockups */}
      <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-gray-100 py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <div className="text-center lg:text-left">
              <h1 className="text-6xl md:text-8xl font-bold mb-4 tracking-tighter">
                ALEXI
              </h1>
              <p className="text-2xl md:text-3xl text-gray-700 mb-4 font-light">
                {t.hero.subtitle}
              </p>
              <p className="text-lg text-gray-600 mb-6">
                {t.hero.tagline}
              </p>
              <p className="text-sm text-gray-500 mb-8 uppercase tracking-wider">
                {t.hero.location}
              </p>
              
              <Link
                to="/gallery"
                className="inline-flex items-center space-x-2 px-8 py-4 bg-black text-white rounded-md hover:bg-gray-800 transition-all transform hover:scale-105 text-lg font-medium"
              >
                <span>{t.hero.cta}</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>

            {/* Mockup Carousel */}
            <div className="relative">
              <div className="relative aspect-[4/3] rounded-lg overflow-hidden shadow-2xl bg-gray-100">
                {mockups.map((mockup, idx) => (
                  <img
                    key={idx}
                    src={mockup}
                    alt={`Artwork mockup ${idx + 1}`}
                    className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-1000 ${
                      idx === currentMockup ? 'opacity-100' : 'opacity-0'
                    }`}
                  />
                ))}
                
                {/* Navigation Arrows */}
                <button
                  onClick={prevMockup}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 text-white rounded-full transition-all"
                  aria-label="Previous mockup"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={nextMockup}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 text-white rounded-full transition-all"
                  aria-label="Next mockup"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>

                {/* Dots Indicator */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
                  {mockups.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentMockup(idx)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        idx === currentMockup ? 'bg-white w-8' : 'bg-white/50'
                      }`}
                      aria-label={`Go to mockup ${idx + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Series Preview - Reduced */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">Featured Series</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { title: 'Saint Martin & Cityscapes', color: 'from-orange-400 to-blue-500' },
              { title: 'Rise in Saint Martin', color: 'from-pink-400 to-orange-500' },
              { title: 'Bloc & Structures', color: 'from-blue-400 to-gray-500' }
            ].map((series, idx) => (
              <Link
                key={idx}
                to="/gallery"
                className="group relative h-48 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${series.color} opacity-90 group-hover:opacity-100 transition-opacity`}></div>
                <div className="absolute inset-0 flex items-end p-4">
                  <h3 className="text-lg font-bold text-white">{series.title}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
